import { Image, Money, GetProductQuery, Video } from '../schema';
import { BigCommerceLocaleString } from './locale';

export type ProductNode = Extract<
  GetProductQuery['site']['route']['node'],
  { __typename: 'Product' }
>;

export type ProductVariables = {
  locale: BigCommerceLocaleString;
  slug: string;
};

export enum ProductImageType {
  PRI = 'PRI',
  ALT = 'ALT',
  LFS = 'LFS',
}

/** Pipe / variant: image not tied to a colour chip (lifestyle, etc.). */
export const PDP_SWATCH_NO_COLOUR = 'NO_COLOUR';

export interface MecImage
  extends Image,
    Partial<Pick<ProductImage, 'is_thumbnail' | 'sort_order'>> {
  displayForLocale?: string;
  swatchMatch?: string;
  /** @deprecated Prefer `imageType === ProductImageType.PRI`; set only for legacy non-pipe data. */
  primary?: boolean;
  imageType?: ProductImageType;
  width?: number | null;
  height?: number | null;
  /** Optional PDP feature label from pipe-delimited image alt (Akeneo label → BC description). */
  tag?: string;
}

/** Primary / hero slot: pipe-delimited `PRI` or legacy `primary` flag. */
export const isPrimaryProductImage = (image: MecImage): boolean =>
  image.imageType === ProductImageType.PRI || Boolean(image.primary);

export type MecVideo = Video;

export enum PriceType {
  Regular = 'regular',
  Sale = 'sale',
  Clearance = 'clearance',
}
export interface MecColour {
  id: number;
  name: string;
  isDefault: boolean;
  swatchImage?: string | null;
  hexColours: string[];
  price: PriceGroup | null;
  availableForSaleOnline: boolean;
  promoBadgeMember: PromoBadge | null;
  promoBadgeGuest: PromoBadge | null;
}

export interface PromoBadge {
  code: string;
  discount_percentage: number;
  multi_discount: boolean;
  start_date: string;
  end_date: string;
  variant_id: number;
  sku: string;
  color_option_id: number;
  is_member_price?: boolean;
  isMemberBadge?: boolean;
}

export interface MecSize {
  id: number;
  name: string | null;
}

export enum InventoryStatus {
  IN_STOCK = 'IN_STOCK',
  LOW_STOCK = 'LOW_STOCK',
  OUT_OF_STOCK = 'OUT_OF_STOCK',
  NOT_STOCKED = 'NOT_STOCKED',
}

export interface VariantImage extends Image {
  thumbnail?: string;
}

export interface StoreInventoryCount {
  store_id: string;
  on_hand: number;
  avg_8_week: number;
  is_stocked: boolean;
}

export interface StoreInventoryInfo {
  status: InventoryStatus;
  quantity: number;
}

export interface MecVariant {
  entityId: number | undefined;
  defaultImage: VariantImage | undefined | null;
  sizeId: number | null | undefined;
  sku: string | null;
  colourId: number | null | undefined;
  price: PriceGroup;
  availableForSaleOnline: boolean;
  hasStockAvailable: boolean;
  promoBadgeMember: PromoBadge | null;
  promoBadgeGuest: PromoBadge | null;
  quickPickup: boolean;
  storeInventory: { [index: string]: StoreInventoryInfo };
  inventoryAggregated: VariantInventory | null;
  isMarketplace: boolean;
  isDropShip: boolean;
  marketplaceSellerName: string | null;
  /** Null when the variant has no restriction or FEATURE_FLAG_PFS is off. */
  fulfillmentRestriction?: FulfillmentRestriction | null;
}

export interface PriceGroup {
  lowPrice?: MecPrice | null;
  highPrice?: MecPrice | null;
  baseLowPrice?: MecPrice | null;
  baseHighPrice?: MecPrice | null;
  priceType: PriceType;
  epochEndDate: number | null;
  isoEndDate: string | null;
  price?: MecPrice | null;
  basePrice?: MecPrice | null;
}

export type MecPrice = Pick<Money, 'value' | 'currencyCode'>;

export interface TechSpec {
  key: string;
  value: string | string[];
  description?: string;
}

export interface ProductNotes {
  keyPointsIntendedUse: string;
  similarProduct: string;
  whatHasChanged: string;
  designMaterialStory: string;
  featuresBenefits: string;
  moreAboutProduct: string;
}

export interface ProductAvailability {
  available_on_plp: boolean;
  available_online_purchase: boolean;
  available_online_search: boolean;
  available_store_purchase: boolean;
  can_backorder: boolean;
  description: string;
  message: string;
  message_severity: string;
  sku_availability_code: string;
  updated_at: string;
  is_marketplacer_sku: boolean;
  is_edi_dropship_sku: boolean;
}

export interface LocalizedFields {
  merchandising?: {
    size_options?: MerchandisingSizeOptionsRaw;
    /** Localized editorial labels (e.g. "Staff Pick") from the `merchandising` metafield. */
    product_tags?: string[];
  };
  techSpecs: TechSpec[];
  feature_data: FeatureSpec;
  overrides: {
    slug: string;
    name: string;
    web_name: string;
    description?: string;
    url_encoded?: string;
    url: string;
    colour?: {
      label_en: string;
      label_fr: string;
    }[];
    size?: {
      label_en: string;
      label_fr: string;
    }[];
    short_desc: string;
  };
  product_notes: ProductNotes;
}

export type MerchandisingSizeOptionsRaw = Record<
  string,
  | { option: string; value: string }[]
  | { size_options?: { option: string; value: string }[] }
>;

export interface SizeSelectionMerchandising {
  axisPrimary: string;
  axisSecondary: string;
  combinations: Array<{
    sizeId: number;
    primaryValue: string;
    secondaryValue: string;
  }>;
}

export enum AlertStates {
  'INFORMATION',
  'WARNING',
  'ERROR',
  'SUCCESS',
}

export interface ShippingRestrictions {
  description: string;
  eligible_free_shipping: boolean;
  message_en: string;
  message_fr: string;
  message_type: keyof typeof AlertStates;
  pickup_allowed: boolean;
  quick_pickup: boolean;
  restriction_level: string;
  shipping_allowed: boolean;
  updated_at: string;
}

export const FulfillmentRestrictionCode = {
  /** "Pick from Store Only Does Not Ship" (fuel, boats): never ships. */
  PICKUP_ONLY: '3',
  /** "Pick from Store when WDC is OOS" (food, socks): ships only while the WDC has stock. */
  SHIPS_FROM_WDC_ONLY: '4',
} as const;

/**
 * Variant-level `non-localized/fulfillment_restriction` metafield. Integrations
 * rewrites it in the same job as store inventory.
 */
export interface FulfillmentRestriction {
  fulfillment_restriction_code: string;
  /** Always false for code 3. For code 4, true only while the WDC has stock. */
  ship_to_address: boolean;
  /** MEC store numbers (Prismic `mec_store_id`), already filtered to stores with stock. */
  pickup_location_ids: number[];
}

export const ReturnRestrictions = {
  HYGIENE: 'hygiene',
  CONSUMABLES: 'consumables',
  E_BIKES: 'e_bikes',
  OVERSIZED: 'oversized',
  HAZARDOUS: 'hazardous',
  ELECTRONICS: 'electronics',
} as const;

export interface MetaFields {
  erp: {
    mms_category_class: string;
    status: string;
    return_restriction_code?: (typeof ReturnRestrictions)[keyof typeof ReturnRestrictions];
    bill29_repair_disclosure_flag: string;
  };
  availability: ProductAvailability;
  size_chart: SizeDataSpec;
  shipping_restrictions: ShippingRestrictions;
  variant_badges?: {
    badges: PromoBadge[];
  };
  variant_badges_guest?: {
    badges: PromoBadge[];
  };
  color_badges?: {
    badges: PromoBadge[];
  };
  color_badges_guest?: {
    badges: PromoBadge[];
  };
  seller: { [id: string]: { name: string } };
  review_ratings?: {
    rating: number;
    count: number;
  };
  merchandising?: {
    compare_tables?: string[];
  };
}

export interface VariantMetaFields {
  price?: {
    type: string;
    valid_period: {
      startDate: string;
      endDate: string;
    };
  };
  availability: ProductAvailability;
  fulfillment_restriction?: unknown;
}

export interface Breadcrumbs {
  entityId: number;
  name: string;
  path: string;
}

export interface Category {
  node: {
    entityId: number;
    breadcrumbs: { edges: { node: { entityId: number; name: string } }[] };
  };
}

export interface ReviewSummary {
  summationOfRatings: number;
  numberOfReviews: number;
}

export interface GiftCardProduct {
  giftCardRecipientsNameOptionId?: number;
  giftCardSendersNameOptionId?: number;
  giftCardRecipientsEmailOptionId?: number;
  giftCardDeliveryDateOptionId?: number;
  giftCardMessageOptionId?: number;
  giftCardProductIdOptionId?: number;
}

export interface BrandProduct {
  entityId?: number;
  name?: string;
}

export interface ProductCategory {
  entityId?: number;
  name?: string;
  path?: string;
  seo?: {
    pageTitle?: string;
    metaKeywords?: string;
    metaDescription?: string;
  };
}

export enum AvailabilityStatus {
  available = 'Available',
  preorder = 'Preorder',
  unavailable = 'Unavailable',
}

export enum Tags {
  tags = 'tags',
  tags_fr = 'tags_fr',
}

export enum Hierarchy {
  primary = 'primary',
  secondary = 'secondary',
}

export enum TagColour {
  red = 'red',
  black = 'black',
  grey = 'grey',
  redOutline = 'redOutline',
}

export type ProductTag = {
  id: number;
  name: string;
  path: string;
  hierarchy: Hierarchy;
  colourOverride?: TagColour | null;
  isLink: boolean;
  start_date?: string;
  end_date?: string;
} | null;

export type ProductTagFull =
  | (ProductTag & ProductTagFromFile & ProductTagFromHit)
  | null;

export type ProductTagFromFile = ProductTag & {
  variants: string[];
};

export type ProductTagFromHit =
  | (ProductTag & {
      colours?: number[];
    })
  | null;

export type PdpCollection = {
  linkText?: string;
  description?: string;
  url?: string;
  displayCollection?: boolean;
};

export interface MECProductColour {
  name: string;
  id: number;
}

export interface MinPurchaseQuantityDropdownOption {
  value?: string | null;
  name?: string | null;
}

export interface MecProduct {
  entityId: number;
  name: string;
  availabilityStatus: AvailabilityStatus;
  seoName: string;
  gaName: string;
  slug: string;
  hreflang: string | null;
  description: string;
  shortDescription: string | null;
  categories?: string[];
  categoryTargets?: string[];
  allColours?: MECProductColour[];
  colours: MecColour[];
  colourOptionId?: number | null;
  sizes: MecSize[];
  /** When set, PDP uses two-axis selector (axisPrimary then axisSecondary) instead of a single size grid. */
  sizeSelectionMerchandising?: SizeSelectionMerchandising | null;
  /** Localized editorial labels from the `merchandising` metafield's `product_tags` (e.g. "Staff Pick"). Distinct from {@link productTags}, which are promotion/discount tags. */
  merchandisingTags: string[];
  sizeOptionId?: number | null;
  variants: MecVariant[];
  images: MecImage[];
  price: PriceGroup;
  techSpecs: TechSpec[];
  productNotes: ProductNotes;
  brand?: BrandProduct | null;
  breadcrumbs: Breadcrumbs[];
  isBike: boolean;
  reviewSummary: ReviewSummary;
  erpCategory: string;
  quebecRepairDisclosureFlag: boolean;
  returnRestrictionCode?:
    | (typeof ReturnRestrictions)[keyof typeof ReturnRestrictions]
    | null;
  giftCard: GiftCardProduct | null;
  isGiftCard: boolean;
  isInStock: boolean;
  availableForSaleOnline: boolean;
  sizeData: SizeDataSpec;
  featureData: FeatureSpec;
  shippingRestrictions: ShippingRestrictions;
  code?: string;
  isDiscontinued: boolean;
  isDisabled: boolean;
  isVisible: boolean;
  productTags: ProductTagFull[];
  pdpCollection: PdpCollection;
  brandURL?: string;
  infoVideoId?: string | null;
  madeInCanada: boolean;
  isCanadianBrand: boolean;
  minPurchaseQuantity: number | null;
  compareTable: string[];
}

/**
 * The subset of {@link MecProduct} returned by `GET /api/product/[code]?view=quick-add`.
 * Single source of truth for both the API handler's field pick and the
 * Quick Add client hooks. Excludes heavy PDP-only data (images, videos,
 * breadcrumbs, tech specs, descriptions).
 */
export const QUICK_ADD_PRODUCT_FIELDS = [
  'entityId',
  'name',
  'gaName',
  'code',
  'slug',
  'brand',
  'categories',
  'price',
  'colours',
  'sizes',
  'variants',
  'colourOptionId',
  'sizeOptionId',
  'sizeSelectionMerchandising',
  'isGiftCard',
  'minPurchaseQuantity',
  'availableForSaleOnline',
  'availabilityStatus',
  'isInStock',
  'isVisible',
  'shippingRestrictions',
] as const satisfies readonly (keyof MecProduct)[];

export type QuickAddProduct = Pick<
  MecProduct,
  (typeof QUICK_ADD_PRODUCT_FIELDS)[number]
>;

/** Used when adding to comparison: full product (PDP) or minimal { code } (e.g. search). */
export type ProductForComparison = MecProduct | { code: MecProduct['code'] };

export function isFullMecProduct(p: ProductForComparison): p is MecProduct {
  return (
    'images' in p &&
    'slug' in p &&
    'name' in p &&
    'price' in p &&
    'techSpecs' in p &&
    'reviewSummary' in p
  );
}

export interface VariantInventory {
  availableToSell: number;
  warningLevel?: number | null;
}

export interface SizeDataSpec {
  size_chart: string;
  fit: boolean;
  rise: boolean;
}

export interface FeatureSpec {
  gender: string;
}

export interface ProductImage {
  id: number;
  product_id: number;
  is_thumbnail: boolean;
  sort_order: number;
  description: string;
  image_file: string;
  url_zoom: string;
  url_standard: string;
  url_thumbnail: string;
  url_tiny: string;
  date_modified: string;
}
